import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Collapse } from 'reactstrap';
import { connect } from 'react-redux';

import withModal from '../../../commons/hocModal';

import EditionMode from '../../../commonComponents/Reservation/EditionMode';
import VisualMode from '../../../commonComponents/Reservation/VisualMode';
import SpecificReservationData from '../SpecificReservationData';
import ButtonCustom from '../../../commonComponents/ButtonCustom';
import TabsComponent from '../../../commonComponents/TabsComponent';
import RoomsManager from '../../../commonComponents/RoomsManager';
import PaxesManager from '../../../commonComponents/PaxesManager';
import ServicesManager from '../../../commonComponents/ServicesManager';

import { translate } from '../../../commons/localize';
import { reservationTTOOType, mapDispatchTTOO } from '../../models/Reservation.model';

import {
  checkFullValidation,
  roomsDataValidation,
  paxesValidation,
  getAvailableRoomsList,
  getAvailableMealPlansList,
  getAvailableServicesList,
  checkIfDatesHaveToUpdate,
  getReservationDatesLimitsByContract,
  handleServicesByRoomDistribution,
  handleServicesByRoomMealPlan
} from './utils';

import { PAXES_ADULT_TYPE, PAXES_CHILDREN_TYPE } from '../../../commons/utils';

import '../../../commonComponents/Reservation/index.css';

const Reservation = (props) => {
  const { reservation, defaultLang, operator } = props;
  const { handleReservationDates: handleReservationDatesFromProps } = props;

  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    const { id, fromDate, contract } = reservation;
    const datesToModify = checkIfDatesHaveToUpdate(fromDate, contract);

    if (!datesToModify) return;

    handleReservationDatesFromProps(id, datesToModify.fromDate, datesToModify.toDate);
  }, [reservation, handleReservationDatesFromProps]);

  const {
    id,
    locator,
    editionMode,
    dataLoaded,
    readOnly,
    rooms,
    contract
  } = reservation;

  const toggleReservationMode = () => {
    if (dataLoaded) {
      props.handleReservationViewMode(id);
      return;
    }

    props.loadReservationData(
      id,
      locator,
      operator,
      (errorMessage) => {
        props.handleModalMessages('errorModal', 'errorCargarFullReserva', errorMessage, 'entendido');
      }
    );
  };

  const handleReservationDates = (_, fromDate, toDate) => {
    props.handleReservationDates(id, fromDate, toDate);
  
    props.handleUpdateServicesByDates(id, fromDate, toDate);
  };

  const handleReservationRoomMealPlan = (_, roomId, mealPlan) => {
    props.handleReservationRoomMealPlan(id, roomId, mealPlan);
    
    handleReservationServicesByMealPlan(roomId, mealPlan);
  };

  const handleReservationRoomDistribution = (_, roomId, distributionData) => {
    props.handleReservationRoomDistribution(id, roomId, distributionData);

    handleReservationServicesByDistribution(roomId, distributionData);
  };

  const handleReservationServicesByMealPlan = (roomId, mealPlan) => {
    if (!contract) return;

    const resultList = handleServicesByRoomMealPlan(roomId, rooms, mealPlan, contract);
    
    if (!resultList) return;
 
    props.handleUpdateServicesList(id, roomId, resultList);
  };

  const handleReservationServicesByDistribution = (roomId, distributionData) => {
    if (!contract) return;

    const {adults, children} = distributionData;

    if (adults === undefined && children === undefined) return;

    const servicePaxType = (children !== undefined) ? PAXES_CHILDREN_TYPE : PAXES_ADULT_TYPE;
    const maxOccupancy = (children !== undefined) ? children : adults;

    const resultList = handleServicesByRoomDistribution(roomId, rooms, servicePaxType, maxOccupancy, contract);

    if (!resultList) return;

    props.handleUpdateServicesList(id, roomId, resultList);
  };

  const handleReservationBackUpAction = (id) => {
    props.handleReservationBackUp(id, reservationTTOOType)
  };

  const handleReservationAppraise = () => {
    const allIsValid = checkFullValidation(rooms);

    setIsValid(allIsValid);

    if (!allIsValid) return;

    props.getReservationAppraise(
      reservation,
      (errorMessage) => {
        props.handleModalMessages('errorModal', 'errorCargarValoracion', errorMessage, 'entendido');
      }
    );
  };

  const contentList = [
    {
      tabTitle: `${translate('habitaciones')} (${(rooms !== undefined) ? rooms.length : ''})`,
      tabId: 'rooms-tab',
      errorTab: (isValid) ? false : !roomsDataValidation(rooms),
      childComponent:
        <RoomsManager
          reservationId={id}
          roomsList={rooms}
          availableRoomsList={getAvailableRoomsList(props.hotelRoomsList, contract)}
          availableMealPlansList={getAvailableMealPlansList(contract)}
          readOnly={readOnly}
          hasToValidate={!isValid}
          handleRoomType={props.handleReservationRoomType}
          handleMealPlan={handleReservationRoomMealPlan}
          handleRoomDistribution={handleReservationRoomDistribution}
          handleRemoveRoom={props.handleReservationRemoveRoom}
        />,
      managementButton: 
        <ButtonCustom
          label={translate('nuevaHabitacion')}
          dataTestLabel="boton-nueva-habitacion"
          colorClass="create-bttn"
          iconClass="create-icon"
          clickAction={() => { props.handleReservationNewRoom(id); }}
        />
    },
    {
      tabTitle: translate('paxes'),
      tabId: 'paxes-tab',
      errorTab: (isValid) ? false : !paxesValidation(rooms),
      childComponent:
        <PaxesManager
          reservationId={id}
          roomsList={rooms}
          readOnly={readOnly}
          hasToValidate={!isValid}
          handleRoomPaxData={props.handleReservationPaxData}
          handleRoomPaxList={props.handleReservationsPaxList}
        />
    }
  ];

  if (contract?.galaDinner === 'NOT_INCLUDED') {
    contentList.push({
      tabTitle: translate('servicios'),
      tabId: 'services-tab',
      childComponent: 
        <ServicesManager
          reservationId={id}
          fromDate={reservation.fromDate}
          toDate={reservation.toDate}
          roomsList={rooms}
          availableServicesList={getAvailableServicesList(contract)}
          readOnly={readOnly}
          handleAddService={props.handleAddService}
          handleDeleteService={props.handleEraseService}
          handleUpdateService={props.handleUpdateService}
        />
    });
  }

  const tabsDataProps = {
    contentList,
    readOnly,
    blockedEdition: (!contract || !Object.keys(contract).length)
  };

  const specificDataProps = {
    reservation,
    defaultLang,
    readOnly,
    handleBookingDate: props.handleBookingDate
  };

  const customContainerClass = `reservation-info__container ${(editionMode) ? '' : 'visual-mode'}`;

  return (
    <Container fluid className={customContainerClass}>
      <Collapse isOpen={editionMode}>
        {editionMode
          && (
            <EditionMode
              defaultLang={defaultLang}
              reservation={reservation}
              datesLimits={getReservationDatesLimitsByContract(contract)}
              firstValorate={true}
              preventButtonActions={(!contract || !Object.keys(contract).length)}
              readOnly={readOnly}
              handleDates={handleReservationDates}
              handleComment={props.handleReservationComment}
              handleCreation={props.setNewReservation}
              handleModification={props.updateExistingReservation}
              handleUndoChanges={handleReservationBackUpAction}
              handleElimination={props.handleReservationElimination}
              handleCancellation={props.cancelReservation}
              handleAppraise={handleReservationAppraise}
              modalManager={props.handleModalMessages}
              specificDataComponent={<SpecificReservationData {...specificDataProps} />}
              tabsDataComponent={<TabsComponent {...tabsDataProps} />}
            />
          )
        }
      </Collapse>
      {(!editionMode) && 
        <VisualMode
          reservation={reservation}
          firstValorate={true}
          availableRoomsList={props.hotelRoomsList}
          handleMode={toggleReservationMode}
        />
      }
    </Container>
  );
};

Reservation.propTypes = {
  reservation: PropTypes.object,
  defaultLang: PropTypes.string,
  operator: PropTypes.string,
  hotelRoomsList: PropTypes.arrayOf(PropTypes.object),
  handleModalMessages: PropTypes.func,
  handleReservationDates: PropTypes.func
};

Reservation.defaultProps = {
  reservation: {},
  defaultLang: 'es',
  operator: '',
  hotelRoomsList: [],
  handleModalMessages: () => {},
  handleReservationDates: () => {}
};

const mapStateToProps = state => ({
  defaultLang: state.session.userDefaultLang,
  operator: state.tourOperation.operator,
  hotelRoomsList: state.tourOperation.hotelRoomsList
});

const mapDispatchToProps = dispatch => ({
  ...mapDispatchTTOO(dispatch)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withModal(Reservation));
export { Reservation, mapStateToProps, mapDispatchToProps};
